import { useRef, useState, useCallback } from 'react'

interface Props {
  onFileSelect: (file: File) => void
  accept: string
  maxSizeMb?: number
  isUploading?: boolean
  disabled?: boolean
  compact?: boolean
}

/**
 * Drag-and-drop file upload zone with click fallback.
 * Native HTML5 drag-drop API — no external libraries.
 */
export function FileUploadZone({
  onFileSelect,
  accept,
  maxSizeMb = 25,
  isUploading = false,
  disabled = false,
  compact = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const acceptList = accept.split(',').map((s) => s.trim())

  const validateAndSelect = useCallback(
    (file: File) => {
      setError(null)

      // Size check
      if (file.size > maxSizeMb * 1024 * 1024) {
        setError(`File exceeds ${maxSizeMb}MB limit.`)
        return
      }

      // Basic MIME check (server does authoritative check)
      if (acceptList.length > 0 && !acceptList.some((mime) => file.type === mime || file.type.startsWith(mime.replace('*', '')))) {
        const labels = acceptList.map((m) => m.split('/')[1]?.toUpperCase() ?? m).join(', ')
        setError(`Invalid file type. Accepted: ${labels}`)
        return
      }

      onFileSelect(file)
    },
    [onFileSelect, maxSizeMb, acceptList],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      if (disabled || isUploading) return
      const file = e.dataTransfer.files[0]
      if (file) validateAndSelect(file)
    },
    [disabled, isUploading, validateAndSelect],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) validateAndSelect(file)
      // Reset so the same file can be re-selected
      e.target.value = ''
    },
    [validateAndSelect],
  )

  const isDisabled = disabled || isUploading

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          if (!isDisabled) setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !isDisabled && inputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg transition-colors cursor-pointer
          ${compact ? 'px-3 py-2' : 'px-4 py-4'}
          ${isDisabled ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' : ''}
          ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={isDisabled}
        />
        <div className={`flex items-center ${compact ? 'gap-2' : 'flex-col gap-1'} text-center`}>
          {isUploading ? (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Uploading...
            </div>
          ) : (
            <>
              <svg className={`${compact ? 'h-4 w-4' : 'h-6 w-6'} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>
                {compact ? 'Upload' : 'Drag and drop or click to upload'}
              </span>
            </>
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-500" role="alert">{error}</p>}
    </div>
  )
}
